/** Dragging an activity onto the stage, and where letting go puts it (`WEB-012` slice 5). */

import { FULL_CANVAS, dragged, type Placement } from "./arrangement";

/** The one dataTransfer type a stage drop reads. */
export const ACTIVITY_DRAG = "application/x-nebulize-activity";

/** An activity already added to this space. */
export interface AddedDrag {
  readonly from: "added";
  readonly addedActivityId: string;
}

/** A catalog entry not added yet. */
export interface CatalogDrag {
  readonly from: "catalog";
  readonly activityId: string;
  readonly version: string;
  readonly name: string;
}

export type ActivityDrag = AddedDrag | CatalogDrag;

/** What a drag carries, on the dataTransfer of the source it started from. */
export interface DragData {
  setData: (format: string, value: string) => void;
  getData: (format: string) => string;
}

export function writeDrag(data: DragData, drag: ActivityDrag): void {
  data.setData(ACTIVITY_DRAG, JSON.stringify(drag));
}

/** The drag this drop carries, or null for anything else. */
export function readDrag(data: DragData | null | undefined): ActivityDrag | null {
  if (data === null || data === undefined) return null;
  const raw = data.getData(ACTIVITY_DRAG);
  if (raw === "") return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const held = parsed as Partial<AddedDrag> & Partial<CatalogDrag>;
  if (held.from === "added" && typeof held.addedActivityId === "string") {
    return { from: "added", addedActivityId: held.addedActivityId };
  }
  if (
    held.from === "catalog" &&
    typeof held.activityId === "string" &&
    typeof held.version === "string" &&
    typeof held.name === "string"
  ) {
    return { from: "catalog", activityId: held.activityId, version: held.version, name: held.name };
  }
  return null;
}

/** A point on the stage, as a fraction of it. */
export interface StagePoint {
  readonly x: number;
  readonly y: number;
}

/** Where a pointer let go, in canvas fractions. Null when the stage has no measurable box. */
export function pointIn(box: DOMRect, clientX: number, clientY: number): StagePoint | null {
  if (box.width === 0 || box.height === 0) return null;
  return {
    x: Math.min(Math.max((clientX - box.left) / box.width, 0), 1),
    y: Math.min(Math.max((clientY - box.top) / box.height, 0), 1),
  };
}

/** What a never-sized activity is given on its first drop. PROPOSED, `../foundations/space.md`. */
export const DROPPED_EXTENT = 0.3;

/** The safe inset a first drop stays inside. PROPOSED, `../foundations/space.md`. */
export const SAFE_INSET = 0.05;

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/** The same rectangle, held off the edges a broadcast scene already owns. */
function withinSafeArea(held: Placement): Placement {
  return {
    ...held,
    left: clamp(held.left, SAFE_INSET, Math.max(SAFE_INSET, 1 - SAFE_INSET - held.width)),
    top: clamp(held.top, SAFE_INSET, Math.max(SAFE_INSET, 1 - SAFE_INSET - held.height)),
  };
}

/** This activity's rectangle, or the first-drop default, centred on the point and clamped. */
export function droppedAt(held: Placement | undefined, point: StagePoint): Placement {
  const start =
    held ?? { ...FULL_CANVAS, width: DROPPED_EXTENT, height: DROPPED_EXTENT };
  const moved = dragged(
    start,
    "move",
    point.x - (start.left + start.width / 2),
    point.y - (start.top + start.height / 2),
  );
  return held === undefined ? withinSafeArea(moved) : moved;
}
